import type { ReactNode } from 'react'

import { Box, Flex, Text } from '@enechain/ecloud-designsystem'
import {
  FaCircleCheckSolid,
  FaCircleExclamationSolid,
  FaCircleInfoSolid,
  FaTriangleExclamationSolid,
} from '@enechain/ecloud-icons'

import { iconSize } from '~/constants/iconSize'

export type CompactAlertStatus =
  | 'info'
  | 'warning'
  | 'error'
  | 'success'
  | 'neutral'

type Tone = {
  color: string
  iconColor: string
  backgroundColor: string
  titleColor: string
  Icon: typeof FaCircleInfoSolid
}

/** 状態ごとの色とアイコン (React mock `CompactAlert` と同じ)。 */
const TONE: Record<CompactAlertStatus, Tone> = {
  info: {
    color: 'Text.infoMid',
    iconColor: 'Text.infoMid',
    backgroundColor: 'Surface.infoLow',
    titleColor: 'Text.infoMid',
    Icon: FaCircleInfoSolid,
  },
  warning: {
    color: 'Text.warningMid',
    iconColor: 'Object.warningMid',
    backgroundColor: 'Surface.warningLow',
    titleColor: 'Text.warningHigh',
    Icon: FaTriangleExclamationSolid,
  },
  error: {
    color: 'Text.negativeMid',
    iconColor: 'Object.negativeMid',
    backgroundColor: 'Surface.negativeLow',
    titleColor: 'Text.negativeMid',
    Icon: FaCircleExclamationSolid,
  },
  success: {
    color: 'Text.positiveMid',
    iconColor: 'Object.positiveMid',
    backgroundColor: 'Surface.positiveLow',
    titleColor: 'Text.positiveMid',
    Icon: FaCircleCheckSolid,
  },
  neutral: {
    color: 'Text.neutralMid',
    iconColor: 'Object.neutralMid',
    backgroundColor: 'Surface.neutralLow',
    titleColor: 'Text.neutralMid',
    Icon: FaCircleInfoSolid,
  },
}

type Props = {
  /** 既定は info。 */
  status?: CompactAlertStatus
  title?: string
  description?: ReactNode
  /** 右端の導線。Tertiary sm を 2 個まで。 */
  actions?: ReactNode
  /** actions と同じ右端に出す (計画提出の呼び出しは導線を children で渡している)。 */
  children?: ReactNode
  /**
   * 読み上げ。既定は無し (初期表示の通知を一斉に読み上げない。alert-placement.md §0)。操作の後に出る
   * 緊急の通知 (取得失敗・貼り付けで反映できなかった等) だけ `alert`、通常の更新は `status` を渡す。
   */
  role?: 'alert' | 'status'
}

/**
 * ページ・タブ上部の状態通知。見出しと短い説明を 1 つの流れ (1 行。幅が足りなければ自然に折り返す) に置く Alert。
 * 内訳の行は積まない (`details` は dev に持ち込まない。description で行を積む書き方は lint
 * `compactAlert/no-stacked-description` が止める)。内訳は右端の導線 (Tertiary sm) から開くか DS `Alert` にする。ダイアログの中は DS `Alert`。
 * 幅 600px 未満では導線を本文の下へ回す (alert-placement.md §0・§4)。
 */
export const CompactAlert = ({
  status = 'info',
  title,
  description,
  actions,
  children,
  role,
}: Props): React.ReactElement => {
  const tone = TONE[status]
  const Icon = tone.Icon
  const trailing = actions ?? children
  const hasTitle = title !== undefined && title !== ''
  const hasDescription =
    description !== undefined && description !== null && description !== ''
  // 見出しと説明は 1 つの文の流れに並べ、自然に折り返す (alert-placement.md §0「Compact の表示仕様」。
  // プロトタイプの .compact-alert と同じく inline)。flex の折り返しにすると、長い説明が丸ごと次の行に落ちる
  const message = (
    <Box minWidth={0} css={{ overflowWrap: 'anywhere' }}>
      {hasTitle && (
        <Text
          as="span"
          textStyle="compactAlertTitle"
          marginRight={hasDescription ? '8px' : 0}
          color={tone.titleColor}
        >
          {title}
        </Text>
      )}
      {hasDescription && (
        <Text as="span" textStyle="compactAlertBody">
          {description}
        </Text>
      )}
    </Box>
  )
  return (
    <Flex
      alignItems="center"
      flexWrap="wrap"
      flexShrink={0}
      gap="8px"
      // コンテンツ幅いっぱい (alert-placement.md §0)。containerType: inline-size は内容から幅を決めないので、
      // 幅の決まらない置き場所 (横並びの Flex の中央など) で潰れないよう幅を明示する
      width="100%"
      minHeight="40px"
      color={tone.color}
      background={tone.backgroundColor}
      borderRadius="md"
      css={{ containerType: 'inline-size' }}
      paddingX="12px"
      paddingY="4px"
      role={role}
    >
      <Icon
        flexShrink={0}
        boxSize={iconSize.alertStatus}
        color={tone.iconColor}
        aria-hidden
      />
      <Box flex="1" minWidth={0}>
        {message}
      </Box>
      {trailing !== undefined && trailing !== null && trailing !== false && (
        <Flex
          className="compact-alert-actions"
          alignItems="center"
          flexShrink={0}
          gap="8px"
          marginLeft="auto"
          css={{
            '@container (max-width: 599px)': {
              flexBasis: '100%',
              justifyContent: 'flex-end',
              marginLeft: 0,
            },
          }}
        >
          {trailing}
        </Flex>
      )}
    </Flex>
  )
}
